import { useEffect, useState } from 'react';
import { useTerritoryStore } from '../../../store/territoryStore';
import type { AssignmentRule } from '../../../types';

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${
        checked ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function RuleTypeLabel({ ruleType }: { ruleType: AssignmentRule['ruleType'] }) {
  const labels: Record<AssignmentRule['ruleType'], string> = {
    NAMED_ACCOUNT: 'Named Account',
    TERRITORY_ZIP: 'Territory (ZIP)',
    TERRITORY_STATE: 'Territory (State)',
    REGION_FALLBACK: 'Region Fallback',
    UNASSIGNED_POOL: 'Unassigned Pool',
  };
  const colors: Record<AssignmentRule['ruleType'], string> = {
    NAMED_ACCOUNT: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    TERRITORY_ZIP: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
    TERRITORY_STATE: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    REGION_FALLBACK: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300',
    UNASSIGNED_POOL: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[ruleType]}`}
    >
      {labels[ruleType]}
    </span>
  );
}

export default function AssignmentRulesEditor() {
  const { assignmentRules, isLoadingRules, fetchAssignmentRules, updateAssignmentRule } =
    useTerritoryStore();

  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    fetchAssignmentRules();
  }, [fetchAssignmentRules]);

  const handleToggle = async (rule: AssignmentRule) => {
    setTogglingId(rule.id);
    try {
      await updateAssignmentRule(rule.id, { isActive: !rule.isActive });
    } finally {
      setTogglingId(null);
    }
  };

  const sorted = [...assignmentRules].sort((a, b) => a.priority - b.priority);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Rules are evaluated in priority order. Higher priority rules take precedence.
        </p>
      </div>

      {isLoadingRules ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          Loading assignment rules...
        </div>
      ) : sorted.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          No assignment rules found.
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-16">
                  Priority
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Rule
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Description
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-24">
                  Active
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sorted.map((rule) => (
                <tr
                  key={rule.id}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${!rule.isActive ? 'opacity-50' : ''}`}
                >
                  <td className="px-4 py-3 text-sm text-center">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-semibold">
                      {rule.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{rule.name}</td>
                  <td className="px-4 py-3 text-sm">
                    <RuleTypeLabel ruleType={rule.ruleType} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {rule.description ?? <span className="italic text-gray-300 dark:text-gray-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Toggle
                      checked={rule.isActive}
                      onChange={() => handleToggle(rule)}
                      disabled={togglingId === rule.id}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400 dark:text-gray-500">
        Priority order is fixed for v1. Contact your administrator to reorder rules.
      </p>
    </div>
  );
}
